import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  PatientStudyResponseDto,
  GetDetailedEcgRequestDto,
  AsyncThunkConfig,
  StudyStatisticResponseDto,
  StudyComparedEcgResponse,
  StudyComparedEcgWithReferenceResponseDto,
  SaveHolterFilesRequestDto,
} from 'types/types';
import { getStudyItemsByFacilityId, getStudyAdminStatistics } from 'api/controllers/study-controller';
import { caregiverEcgResponseToStudyItemMapper } from 'utils/mappers';
import { HolterFileExtension, ViewMode, UserRole } from 'types/enums';
import { ECGStudyResponseDto } from 'types/types/dto/ecg-study-response-dto';
import { getStudyComparedEcgData } from 'api/controllers/ecg-capture-controller';
import { getCaregiverEcgs } from 'api/controllers/caregiver-controller';
import { getFilteredECGSamples } from 'utils';
import { getHolterFileName } from 'utils/helpers';
import { config } from 'packages/config';

const getPatientsStudyData = createAsyncThunk<PatientStudyResponseDto, undefined, AsyncThunkConfig>(
  'study/getPatientsStudyData',
  async (_, { getState }) => {
    const { study, user } = getState();
    const { studyViewMode, tableNavigation, studyFacilityId } = study;
    const caregiverId = user?.data?.caregiverId;
    const { limit, skip } = tableNavigation;
    let ecgResponse: null | ECGStudyResponseDto = null;
    if (studyViewMode === ViewMode.CAREGIVER && caregiverId) {
      ecgResponse = await getCaregiverEcgs(caregiverId, limit, skip);
    }
    if (studyViewMode === ViewMode.ADMIN && studyFacilityId) {
      ecgResponse = await getStudyItemsByFacilityId(studyFacilityId, limit, skip);
    }

    if (!ecgResponse) {
      return { patients: [], totalCount: 0, studyStatistic: null };
    }
    const patients = ecgResponse.data.map(caregiverEcgResponseToStudyItemMapper);

    return { patients, totalCount: ecgResponse.totalCount, studyStatistic: ecgResponse.studyStatistic };
  },
);

const getDetailedEcgData = createAsyncThunk<
  StudyComparedEcgResponse | StudyComparedEcgWithReferenceResponseDto,
  GetDetailedEcgRequestDto
>('study/getDetailedEcg', async ({ waveformId, isAnalyzeExist }) => {
  const response = await getStudyComparedEcgData({ waveformId, isAnalyzeExist });
  if (!isAnalyzeExist) {
    response.everbeatData.samples = getFilteredECGSamples(
      response.everbeatData.samples,
      response.everbeatData.sampleRate,
    );
  }

  return response;
});

const getStudyStatistic = createAsyncThunk<StudyStatisticResponseDto | null, undefined, AsyncThunkConfig>(
  'study/getStudyStatistic',
  async (_, { getState }) => {
    const { studyViewMode } = getState().study;
    const { data: userData } = getState().user;

    // Only fetch statistics for admin users
    if (studyViewMode === ViewMode.ADMIN && userData?.role === UserRole.ADMIN) {
      return await getStudyAdminStatistics();
    }

    return null;
  },
);

const saveHolterFiles = createAsyncThunk<void, SaveHolterFilesRequestDto, AsyncThunkConfig>(
  'study/saveHolterFiles',
  async ({ markFile, holterEcgFile, patientName }, { extra: { awsS3Bucket } }) => {
    if (markFile) {
      awsS3Bucket.uploadFile(
        markFile,
        config.ENV.AWS.S3.HOLTER_BUCKET,
        getHolterFileName(patientName, HolterFileExtension.MARKER),
      );
    }
    if (holterEcgFile) {
      awsS3Bucket.uploadFile(
        holterEcgFile,
        config.ENV.AWS.S3.HOLTER_BUCKET,
        getHolterFileName(patientName, HolterFileExtension.ECG),
      );
    }
  },
);

export { getDetailedEcgData, getPatientsStudyData, getStudyStatistic, saveHolterFiles };
